import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { isDefined, isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs } from '@trimble-gcs/i18n-types';
import {
  ModusAutocomplete,
  ModusAutocompleteModule,
  ModusButtonModule,
  ModusIconModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { filter, map } from 'rxjs';
import { DialogComponent } from '../../../../dialog/dialog.component';
import { DialogData } from '../../../../dialog/dialog.model';
import { DialogService } from '../../../../dialog/dialog.service';
import { modusOptionListTranslations } from '../../../../i18n/modus-option-list.translations';
import { ScandataModel, UpdateScandataModel } from '../../../../scandata/scandata.models';
import { ScandataService } from '../../../../scandata/scandata.service';
import { MAX_TAG_LENGTH, TagService } from '../../../../tag/tag.service';
import { NotWhitespaceStringValidator } from '../../../../utils/not-whitespace-string-validator';
import { taggingTranslations } from './tagging.translations';

@Component({
  selector: 'sd-tagging',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusTooltipModule,
    ModusButtonModule,
    ModusIconModule,
    ModusAutocompleteModule,
    MatProgressBarModule,
    ScrollingModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './tagging.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaggingComponent implements OnDestroy, AfterViewInit {
  private readonly tagService = inject(TagService);
  private readonly scandataService = inject(ScandataService);
  private readonly dialogService = inject(DialogService);
  private readonly translationService = inject(TranslationService);

  private readonly autocomplete = viewChild.required<ModusAutocomplete<string>>('autocomplete');

  private readonly projectTags = toSignal(this.tagService.getTags(), { initialValue: [] });

  private readonly onAnyScan = this.getTagsOnAnyScan();
  private readonly onEveryScan = this.getTagsOnEveryScan();

  private readonly removedTags = this.linkRemovedTags();
  private readonly addableTags = this.getAddableTags();

  readonly translations = { ...taggingTranslations(), ...modusOptionListTranslations() };

  readonly scandataModels = input.required<ScandataModel[]>();
  readonly closeClicked = output();

  readonly isSaving = signal(false);

  readonly existingTags = computed(() => getTagsExcluding(this.onAnyScan(), this.removedTags()));
  readonly hasExistingTags = computed(() => this.existingTags().length > 0);

  readonly addedTags = this.linkAddedTags();
  readonly filteredTags = this.getFilteredTags();
  readonly errorText = this.getErrorText();

  readonly saveDisabled = computed(
    () => this.isSaving() || (this.addedTags().length === 0 && this.removedTags().length === 0),
  );

  readonly tagSelector = new FormControl<string | null>(null, {
    validators: [NotWhitespaceStringValidator, Validators.maxLength(MAX_TAG_LENGTH)],
    updateOn: 'change',
  });

  private readonly tagSelectorValue = toSignal(this.tagSelector.valueChanges, {
    initialValue: null,
  });

  private readonly tagSelectorStatus = toSignal(this.tagSelector.statusChanges);

  private errorDialogRef: MatDialogRef<DialogComponent> | null = null;

  @HostBinding('class') class = 'flex flex-col h-full';

  constructor() {
    this.createTagSelectorTouchedEffect();
  }

  ngAfterViewInit(): void {
    this.observeAutoCompleteKeyDown();
  }

  ngOnDestroy(): void {
    this.errorDialogRef?.close();
  }

  removeExisting(tag: string) {
    this.removedTags.update((value) => [...value, tag]);
  }

  removeAdded(tag: string) {
    this.addedTags.update((added) => {
      const index = added.indexOf(tag);
      if (index >= 0) {
        added.splice(index, 1);
      }
      return [...added];
    });
  }

  addTag(tag: string) {
    if (this.tagSelector.invalid || isNil(tag)) return;

    if (this.onEveryScan().includes(tag) && this.removedTags().includes(tag)) {
      // if the tag was on every scan and subsequently removed
      // we should add it back by removing it from the removedTags
      this.removedTags.update((removed) => getTagsExcluding(removed, [tag]));
    } else {
      this.addedTags.update((value) => [...value, tag]);
    }

    this.tagSelector.reset();
  }

  save() {
    this.setFormSaving(true);

    const updates = this.scandataModels().map((scan) => {
      const tags = isNil(scan.tags) ? [] : getTagsExcluding(scan.tags, this.removedTags());
      const uniqueTags = getUniqueTags([tags, this.addedTags()]);

      const result: UpdateScandataModel = { id: scan.id, tags: uniqueTags };
      return result;
    });

    return this.scandataService.updateScandataModels(updates).subscribe({
      next: (results) => {
        const errors = this.scandataModels().filter((model) => {
          const hasError = results.some(
            (update) => update.updateScandataModel.id === model.id && isDefined(update.error),
          );
          return hasError;
        });

        if (errors.length === 0) return this.closeClicked.emit();

        this.showSaveErrors(errors);
        this.setFormSaving(false);
      },
    });
  }

  private getTagsOnAnyScan(): Signal<string[]> {
    return computed(() =>
      getUniqueTags(
        this.scandataModels()
          .flatMap((scan) => scan.tags)
          .filter(isDefined),
      ),
    );
  }

  private getTagsOnEveryScan(): Signal<string[]> {
    return computed(() =>
      this.onAnyScan().filter((tag) =>
        this.scandataModels().every((scan) => scan.tags?.includes(tag)),
      ),
    );
  }

  private linkRemovedTags(): WritableSignal<string[]> {
    return linkedSignal<string[], string[]>({
      source: this.onAnyScan,
      computation: (source, previous) => {
        return previous?.value.filter((tag) => source.includes(tag)) ?? [];
      },
    });
  }

  private getAddableTags(): Signal<string[]> {
    return computed(() => {
      const visibleOnEveryScan = getTagsExcluding(this.onEveryScan(), this.removedTags());
      const exclVisibleOnEveryScan = getTagsExcluding(this.projectTags(), visibleOnEveryScan);
      const addable = getTagsExcluding(exclVisibleOnEveryScan, this.addedTags());
      return addable;
    });
  }

  private linkAddedTags(): WritableSignal<string[]> {
    return linkedSignal<string[], string[]>({
      source: this.onEveryScan,
      computation: (source, previous) => {
        return getTagsExcluding(previous?.value ?? [], source);
      },
    });
  }

  private createTagSelectorTouchedEffect() {
    effect(() => {
      const status = this.tagSelectorStatus();
      if (status === 'INVALID' && this.tagSelector.dirty) {
        this.tagSelector.markAsTouched();
      }
    });
  }

  private getFilteredTags() {
    return computed(() => {
      const availableTags = this.addableTags();
      const filter = this.tagSelectorValue();

      if (isNil(filter)) return availableTags;

      const filterValue = filter.toLowerCase();
      return availableTags.filter((tag) => tag.toLowerCase().includes(filterValue));
    });
  }

  private getErrorText() {
    return computed(() => {
      const status = this.tagSelectorStatus();

      return status === 'INVALID' &&
        this.tagSelector.dirty &&
        this.tagSelector.hasError('maxlength')
        ? this.getLengthExceededErrorMessage()
        : '';
    });
  }

  private getLengthExceededErrorMessage() {
    const text = this.translations.tagInput.lengthExceededError.text;
    return this.translationService.translate(text, { maxCharacters: MAX_TAG_LENGTH });
  }

  private observeAutoCompleteKeyDown() {
    this.autocomplete()
      .inputKeydown$.pipe(
        filter((event) => event.key === 'Enter'),
        filter(() => isNil(this.autocomplete().activeOption)),
        filter(() => this.tagSelector.dirty && this.tagSelector.valid),
        map(() => {
          const value = this.tagSelector.value as string;
          const tag =
            this.projectTags().find((tag) => tag.toLowerCase() === value.toLowerCase()) ?? value;
          return tag;
        }),
        filter((tag) => {
          const inAdded = this.addedTags().find(
            (added) => added.toLowerCase() === tag.toLocaleLowerCase(),
          );
          return !inAdded;
        }),
      )
      .subscribe((tag) => {
        this.addTag(tag);
        this.autocomplete().closePanel();
      });
  }

  private setFormSaving(isSaving: boolean) {
    if (isSaving) {
      this.tagSelector.disable({ emitEvent: false });
    } else {
      this.tagSelector.enable({ emitEvent: false });
    }

    this.isSaving.set(isSaving);
  }

  private showSaveErrors(failedModels: ScandataModel[]) {
    const names = `\n${failedModels.map((model) => model.name).join('\n')}`;
    const pluralArgs: PluralArgs = { count: failedModels.length, names };
    const pluralMap = this.translations.saveErrorDialog.message;
    const message = this.translationService.translatePlural(pluralArgs, pluralMap);

    const dialogData = new DialogData(this.translations.saveErrorDialog.title.text, message, {
      text: this.translations.saveErrorDialog.dismiss.text,
      color: 'danger',
    });

    this.errorDialogRef = this.dialogService.showMessage(dialogData);
  }
}

function getTagsExcluding(array: string[], excludeArray: string[]) {
  return array.filter((value) => {
    return !excludeArray.includes(value);
  });
}

function getUniqueTags(array: string[]): string[];
function getUniqueTags(arrays: string[][]): string[];
function getUniqueTags(arrays: string[] | string[][]): string[] {
  return [...new Set([...arrays.flatMap((val) => val)])];
}
